import { map } from "rxjs/operators";
import { DELETE, GET, POST, PUT } from "../module/services/fetch";
import { TechnicalAddCounterModel } from "../models/TechnicalUsers";
import {
  SecurityQuestionBody,
  UigpOtpBody,
} from "../common/TwoFactorAuth/module/constants";
import { TechnicalUserType } from "../pages/TechnicalUser/module/constants";
import config from "../utils/config";
import { of } from "rxjs";

export interface TechUserPwdMgmtBody {
  password?: string;
  tCode?: string;
  applicationId?: string;
  applicationName?: string;
  tuCode: string;
  identitySystemId: string;
}

export const technicalUsersByOuIdService = ({
  ouCode,
  tiType,
}: {
  ouCode: string;
  tiType?: TechnicalUserType;
}) => {
  const base = `technical-users/ou/${ouCode}`;
  const url = tiType ? `${base}?tiType=${tiType}` : base;
  // Get technical users by ou code
  return GET(url).pipe(map((response: any) => response));
};

export const technicalUsersEntitlementsService = (tuCode: string) =>
  GET(`technical-users/${tuCode}/entitlements`).pipe(
    map((response: any) => response)
  );

export const technicalUsersCreateService = (body: FormData) =>
  POST(
    "technical-users/",
    body,
    undefined,
    undefined,
    undefined,
    "multipart/form-data"
  ).pipe(map((response: any) => response));

export const technicalUsersUpdateService = (payload: {
  tuCode: string;
  body: FormData;
}) => {
  const { body, tuCode } = payload;
  return POST(
    `technical-users/updateTU/${tuCode}`,
    body,
    undefined,
    undefined,
    undefined,
    "multipart/form-data"
  ).pipe(map((response: any) => response));
};

export const technicalUsersPasswordPolicyService = (
  payload: TechUserPwdMgmtBody
) => {
  // INTEGRATION
  if (config.apiAddress === "intapi.nexi.it") {
    return of({
      minLength: 12,
      maxLength: 15,
      lowerCase: {
        min: 1,
        allowed: true,
      },
      specialChar: {
        min: 1,
        allowed: true,
        allowedCharacters: [
          "$",
          "(",
          ")",
          "*",
          "+",
          "-",
          ".",
          ":",
          "[",
          ";",
          "<",
          "=",
          ">",
          "_",
        ],
      },
      upperCase: {
        min: 1,
        allowed: true,
      },
      numberCase: {
        min: 1,
        allowed: true,
      },
    });
  }

  // STAGING OR PRODUCTION
  const { identitySystemId, tuCode } = payload;
  return GET(
    `technical-users/passwordPolicy?tuCode=${tuCode}&identitySystemId=${identitySystemId}`
  ).pipe(map((response: any) => response));
};

export const technicalUsersResetPwdService = (payload: TechUserPwdMgmtBody) =>
  PUT(`technical-users/reset-password`, payload).pipe(
    map((response: any) => response)
  );

export const technicalUsersChangePwdService = (payload: TechUserPwdMgmtBody) =>
  PUT(`technical-users/change-password`, payload).pipe(
    map((response: any) => response)
  );

export const technicalUsersBlockAccountService = (
  payload: TechUserPwdMgmtBody
) => {
  const { identitySystemId, applicationId } = payload;
  return PUT(
    `technical-users/${applicationId}/block-account?identitySystemId=${identitySystemId}`
  ).pipe(map((response: any) => response));
};

export const technicalUsersUnlockAccountService = (
  payload: TechUserPwdMgmtBody
) => {
  const { identitySystemId, applicationId } = payload;
  return PUT(
    `technical-users/${applicationId}/unlock-account?identitySystemId=${identitySystemId}`
  ).pipe(map((response: any) => response));
};

export const technicalUsersDeleteService = (payload: TechUserPwdMgmtBody) => {
  const { identitySystemId, tuCode, applicationName } = payload;
  return DELETE(
    `technical-users/entitlements/remove?tuCode=${tuCode}&identitySystemId=${identitySystemId}&applicationId=${applicationName}&tiCode=0`
  ).pipe(map((response: any) => response));
};

export const technicalUsersResetPwdVerifyCaMobileOtpService = (body: {
  accountId: string;
  identitySystemId: string;
  password: string;
  otp: string;
}) => POST(`user/resetPassword`, body).pipe(map((response: any) => response));

export const technicalUsersResetPwdVerifySecQuestsService = (
  body: SecurityQuestionBody
) => {
  return POST(`authentication/question/verify/reset-pwd`, body).pipe(
    map((response: any) => response)
  );
};

export const technicalUsersResetPwdVerifyUigpOtpService = (
  body: UigpOtpBody
) => {
  return POST(`authentication/validate-reset-pwd`, body).pipe(
    map((res: any) => res)
  );
};

export const technicalUsersChangePwdVerifyCaMobileOtpService = (body: {
  accountId: string;
  identitySystemId: string;
  password: string;
  otp: string;
}) => POST(`user/changePassword`, body).pipe(map((response: any) => response));

export const technicalUsersChangePwdVerifySecQuestsService = (
  body: SecurityQuestionBody
) => {
  return POST(`authentication/question/verify/change-pwd`, body).pipe(
    map((response: any) => response)
  );
};

export const technicalUsersChangePwdVerifyUigpOtpService = (
  body: UigpOtpBody
) => {
  return POST(`authentication/validate-chg-pwd`, body).pipe(
    map((res: any) => res)
  );
};

export const adminTechnicalUsersGetAllCountersService = () => {
  return GET("technical-users/admin/allCounters").pipe(
    map((response: any) => response)
  );
};

export const adminTechnicalUsersGetOuCountersService = () => {
  return GET("technical-users/admin/ouCounters").pipe(
    map((response: any) => response)
  );
};

export const adminTechnicalUsersGetApplicationCountersService = () => {
  return GET("technical-users/admin/applicationCounters").pipe(
    map((response: any) => response)
  );
};

export const adminTechnicalUsersAddCounterService = (
  newCounter: TechnicalAddCounterModel
) => {
  const { active, counter, isDefault, length, prefix, type } = newCounter;
  const body = {
    active: active === "true" ? true : false,
    counter: Number(counter),
    default: isDefault === "true" ? true : false,
    prefix: prefix,
    type: type,
    length: Number(length),
  };
  return POST(`technical-users/admin/create`, body);
};

export const adminTechnicalUsersUpdateCounterService = (payload: {
  counterCode: string;
  counter: TechnicalAddCounterModel;
}) => {
  const { counter, counterCode } = payload;
  const body = {
    active: counter.active === "true" ? true : false,
    default: counter.isDefault === "true" ? true : false,
    prefix: counter.prefix,
    length: Number(counter.length),
  };
  return PUT(`technical-users/admin/modifyCounter/${counterCode}`, body);
};

export const adminTechnicalUsersDeleteCounterService = (
  counterCode: string
) => {
  return DELETE(
    `technical-users/admin/deleteCounter?counterCode=${counterCode}`
  );
};

export const adminTechnicalUsersGetRiorgIdentityService = () => {
  return GET(`technical-users/admin/getCurrentReorganization`);
};

export const adminTechnicalUsersGetRiorgOusService = () => {
  return GET(`technical-users/admin/reorg/getOus`);
};

export const adminTechnicalUsersRiorgOusService = (payload: {
  tiCode: string;
  ouCode: string;
}) => {
  const { ouCode, tiCode } = payload;
  return POST(`technical-users/admin/save?tiCode=${tiCode}&ouCode=${ouCode}`);
};

export const adminTechnicalUsersGetAllTU = () => {
  return GET("technical-users/admin/getAllTechnicalUsers").pipe(
    map((response: any) => response)
  );
};
